import React from 'react';
import SEO from '../components/SEO';
import PageBanner from '../components/PageBanner';
import Reveal from '../components/ui/Reveal';
import usePage from '../hooks/usePage';
import { UPLOADS_URL } from '../api/axios';

const resolveImage = (src, fallback = '') => {
  if (!src) return fallback;
  if (src.startsWith('http://') || src.startsWith('https://') || src.startsWith('/')) return src;
  return `${UPLOADS_URL}/${src}`;
};

const renderParagraphs = (raw, fallback = '') => {
  const text = raw || fallback;
  const parts = String(text).replace(/\r/g, '').split(/\n\s*\n|\n/).map(s => s.trim()).filter(Boolean);
  return parts.map((p, i) => <p key={i} className="mb-4 text-muted leading-relaxed text-[15px]">{p}</p>);
};

const OtherFacilitiesPage = () => {
  const pageData = usePage('other-facilities');
  const sections = pageData.sections || {};

  const banner = sections.banner || pageData.banner || {};
  const bannerTitle = banner.title || pageData.title || "Transport & Medical Facilities";
  const bannerSubtitle = banner.subtitle || "Prioritizing Safety, Health and Well-Monitored Student Care at Every Step";
  const bannerBreadcrumb = banner.breadcrumb || "OTHER FACILITIES";
  const bannerImgSrc = pageData.bannerImage || '/images/page-banners/other-facilities.jpg';

  const transport = sections.transport || {};
  const transportImg = resolveImage(transport.image, '/images/page-banners/other-facilities.jpg');
  const transportBadges = (transport.badges || 'Real-Time GPS Tracking | Female Attendant on Every Bus | Speed Governors Installed | Regular Mechanical Audits | Emergency First Aid Kit').split('|').map(s => s.trim()).filter(Boolean);

  const medical = sections.medical || {};
  const medicalImg = resolveImage(medical.image, '/images/page-banners/why-choose-us.jpg');
  const medicalBadges = (medical.badges || 'Full-Time Qualified Nurse | Sanitized Recovery Beds | Annual Pediatric Checkups | Emergency Doctor Tie-Up | Maintained Student Health Cards').split('|').map(s => s.trim()).filter(Boolean);

  const safety = sections.safety || {};
  const safetyImg = resolveImage(safety.image, '/images/facilities/Infrastructure.jpg');
  const safetyBadges = (safety.badges || '24/7 CCTV Monitoring | Fire Safety Certified | RO Water Plants | Dedicated Female Guards | First-Aid Station | Gated Entry Control').split('|').map(s => s.trim()).filter(Boolean);

  return (
    <>
      <SEO
        title={pageData.title || "Transport, Medical & Safety Facilities"}
        description="Safe GPS school transport, on-campus medical infirmary, and comprehensive safety protocols at St. Andrews World School, Sonipat."
        keywords="School Transport, School Bus Sonipat, Medical Room, School Clinic, Campus Safety, CCTV Surveillance, SAWS Sonipat"
      />

      <PageBanner
        title={bannerTitle}
        subtitle={bannerSubtitle}
        breadcrumb={bannerBreadcrumb}
        bgImage={bannerImgSrc}
      />


      {/* TRANSPORT SECTION */}
      <section className="section bg-white">
        <div className="container">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-12 items-center">
            <div className="lg:col-span-6">
              <Reveal>
                <div className="relative rounded-2xl overflow-hidden shadow-xl border border-gray-100 group">
                  <img
                    src={transportImg}
                    alt="School Transport"
                    className="w-full h-[400px] object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute top-4 left-4 w-12 h-12 rounded-2xl bg-amber-500 text-white flex items-center justify-center shadow-lg">
                    <i className="fas fa-bus text-xl" />
                  </div>
                </div>
              </Reveal>
            </div>

            <div className="lg:col-span-6">
              <Reveal delayMs={150}>
                <div className="kicker mb-2">{transport.kicker || 'Safe Student Commute'}</div>
                <h2 className="h-section mb-6">{transport.heading || 'GPS-Enabled School Transport Network'}</h2>
                <div className="divider-line mb-6" />
                <div className="space-y-2 mb-6">
                  {renderParagraphs(transport.paragraphs, "We offer safe, reliable, and well-monitored transport arrangements for our students. St. Andrews World School provides pick-up and drop services across Sonipat and nearby areas, driven by certified experienced drivers and operating along optimized routes.\n\nFor the safety and security of our students, unauthorized commercial transporters are strictly barred from school premises. Our transport services are optional and designed with student comfort in mind.")}
                </div>

                {transportBadges.length > 0 && (
                  <div className="flex flex-wrap gap-2 pt-4 border-t border-gray-100">
                    {transportBadges.map((badge, bIdx) => (
                      <span
                        key={bIdx}
                        className="px-3 py-1.5 rounded-full text-xs font-semibold bg-amber-50 text-amber-900 border border-amber-200 inline-flex items-center gap-1.5"
                      >
                        <i className="fas fa-location-dot text-amber-600 text-[10px]" />
                        <span>{badge}</span>
                      </span>
                    ))}
                  </div>
                )}
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* MEDICAL INFIRMARY SECTION */}
      <section className="section bg-surface-alt">
        <div className="container">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-12 items-center">
            <div className="lg:col-span-6 order-2 lg:order-1">
              <Reveal>
                <div className="kicker mb-2">{medical.kicker || 'Health & Infirmary'}</div>
                <h2 className="h-section mb-6">{medical.heading || 'Dedicated Campus Medical Centre'}</h2>
                <div className="divider-line mb-6" />
                <div className="space-y-2 mb-6">
                  {renderParagraphs(medical.paragraphs, "St. Andrews World School never compromises on student wellness. We maintain a sanitized, dedicated medical room with patient beds and essential first-aid equipment.\n\nA full-time qualified nurse is stationed in the infirmary during all school hours to attend to sudden sickness, minor injuries, and everyday student healthcare.\n\nRoutine annual health check-ups and updated student medical cards are maintained.")}
                </div>

                {medicalBadges.length > 0 && (
                  <div className="flex flex-wrap gap-2 pt-4 border-t border-gray-200">
                    {medicalBadges.map((badge, bIdx) => (
                      <span
                        key={bIdx}
                        className="px-3 py-1.5 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-800 border border-emerald-200 inline-flex items-center gap-1.5"
                      >
                        <i className="fas fa-heart-pulse text-emerald-600 text-[10px]" />
                        <span>{badge}</span>
                      </span>
                    ))}
                  </div>
                )}
              </Reveal>
            </div>

            <div className="lg:col-span-6 order-1 lg:order-2">
              <Reveal delayMs={150}>
                <div className="relative rounded-2xl overflow-hidden shadow-xl border border-gray-100 group">
                  <img
                    src={medicalImg}
                    alt="Campus Medical Centre"
                    className="w-full h-[400px] object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute top-4 left-4 w-12 h-12 rounded-2xl bg-emerald-600 text-white flex items-center justify-center shadow-lg">
                    <i className="fas fa-hospital text-xl" />
                  </div>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* SAFETY & SECURITY SECTION */}
      <section className="section bg-white">
        <div className="container">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-12 items-center">
            <div className="lg:col-span-6">
              <Reveal>
                <div className="relative rounded-2xl overflow-hidden shadow-xl border border-gray-100 group">
                  <img
                    src={safetyImg}
                    alt="Campus Safety & Security"
                    className="w-full h-[400px] object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute top-4 left-4 w-12 h-12 rounded-2xl bg-indigo-600 text-white flex items-center justify-center shadow-lg">
                    <i className="fas fa-shield-halved text-xl" />
                  </div>
                </div>
              </Reveal>
            </div>

            <div className="lg:col-span-6">
              <Reveal delayMs={150}>
                <div className="kicker mb-2">{safety.kicker || 'Health, Hygiene & Security'}</div>
                <h2 className="h-section mb-6">{safety.heading || 'Comprehensive Safety & Security Protocols'}</h2>
                <div className="divider-line mb-6" />
                <div className="space-y-2 mb-6">
                  {renderParagraphs(safety.paragraphs, "The safety of every student is our paramount priority. Our campus is guarded 24/7 by trained security personnel, complete with high-definition CCTV coverage across all entry gates, corridors, and communal zones.\n\nFire safety equipment, emergency evacuation plans, sanitized premises, RO purified water, and strict visitor verification protocols ensure an uncompromised, secure learning atmosphere.")}
                </div>

                {safetyBadges.length > 0 && (
                  <div className="flex flex-wrap gap-2 pt-4 border-t border-gray-100">
                    {safetyBadges.map((badge, bIdx) => (
                      <span
                        key={bIdx}
                        className="px-3 py-1.5 rounded-full text-xs font-semibold bg-indigo-50 text-indigo-900 border border-indigo-200 inline-flex items-center gap-1.5"
                      >
                        <i className="fas fa-shield-halved text-indigo-600 text-[10px]" />
                        <span>{badge}</span>
                      </span>
                    ))}
                  </div>
                )}
              </Reveal>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default OtherFacilitiesPage;